<script>
import date from 'locutus/php/datetime/date'
import {
  fetchPolicyBeneficiary,
  fetchPolicyBenefactors,
  fetchPolicyBenefactor,
  createPolicyBenefactor,
  updatePolicyBenefactor,
  deletePolicyBenefactor,
} from '@apis/admin/affiliate'
import { fetchAssets } from '@apis/admin/asset'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  props: {
    beneficiary_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      benefactors: [],
      beneficiary_info: null,
      assets: [],
      target_benefactor_seq: 0,
      show_benefactor_info: false,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    beneficiary_seq() {
      this.init()
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([
        this.loadConditions(),
        this.loadAssets(),
        this.loadBeneficiary(),
      ]).catch((e) => this.$toasted.error(e.message))
    },
    async loadConditions() {
      const { data } = await fetchPolicyBenefactors(null, this.beneficiary_seq)

      this.benefactors = data
    },
    async loadBeneficiary() {
      const { beneficiary_info } = await fetchPolicyBeneficiary(
        null,
        this.beneficiary_seq
      )

      this.beneficiary_info = beneficiary_info
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    handleAddCondition() {
      this.benefactors.push({
        list_order: 0,
        beneficiary_seq: this.beneficiary_seq,
        benefactor_type: undefined,
        base_amount_target: undefined,
        amount_type: undefined,
        asset_seq: undefined,
        member_seq: '',
      })
    },
    async handleDeleteCondition(idx) {
      this.loading = true
      try {
        if (this.benefactors[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deletePolicyBenefactor(
            null,
            this.beneficiary_seq,
            this.benefactors[idx].seq
          )
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.benefactors.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitCondition(idx) {
      this.loading = true
      const params = this.benefactors[idx]
      ;(params.seq ? updatePolicyBenefactor : createPolicyBenefactor)(params)
        .then(({ seq }) =>
          fetchPolicyBenefactor(null, this.beneficiary_seq, seq)
        )
        .then(({ benefactor_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.benefactors, idx, { ...benefactor_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <div v-if="beneficiary_info">
    <table
      v-if="beneficiary_seq"
      cellspacing="0"
      cellpadding="0"
      :class="$style.colTable"
    >
      <thead>
        <tr>
          <th scope="col">{{ $t('affiliate_policy_benefactor.order') }}</th>
          <th scope="col">{{ $t('affiliate_policy_benefactor.benefit') }}</th>
          <th scope="col">{{
            $t('affiliate_policy_benefactor.benefit_type')
          }}</th>
          <th scope="col">
            <div :class="$style.tRight">
              <a
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleAddCondition"
                ><span>{{ $t('admin.add') }}</span></a
              >
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(benefactor, idx) in benefactors"
          :key="benefactor.seq ? `${benefactor.seq}` : `create-${idx}`"
        >
          <td :class="[$style.tCenter, $style.nowrap]">
            <input
              v-model="benefactor.list_order"
              type="text"
              :class="[$style.inputTypeText, $style.w40, $style.tRight]"
            />
          </td>
          <td :class="$style.tCenter">
            <select
              v-model="benefactor.benefactor_type"
              :disabled="benefactor.seq"
            >
              <option :value="undefined">{{
                $t('admin.restriction_select')
              }}</option>
              <option
                v-for="[type, type_title] in constants.affiliate
                  .policy_benefactor.benefactor_type[
                  beneficiary_info.target_action
                ]"
                :key="type"
                :value="type"
              >
                {{ type_title }}
              </option>
            </select>
          </td>
          <td :class="$style.wide">
            <template v-if="benefactor.benefactor_type === 'MEMBER'">
              회원 고유 번호가
              <input
                v-model="benefactor.member_seq"
                type="text"
                :class="[$style.inputTypeText, $style.w80, $style.tRight]"
              />
              인 자가
            </template>
            <select v-model="benefactor.base_amount_target">
              <option :value="undefined">{{
                $t('admin.restriction_select')
              }}</option>
              <option
                v-for="[type, type_title] in constants.affiliate
                  .policy_benefactor.base_amount_target[
                  beneficiary_info.target_action
                ]"
                :key="type"
                :value="type"
              >
                {{ type_title }}
              </option>
            </select>
            <template v-if="benefactor.base_amount_target === '__FIXED__'">
              으로
              <input
                v-model="benefactor.amount"
                type="text"
                :class="[$style.inputTypeText, $style.w120, $style.tRight]"
              />
              {{ ' ' }}
              <select v-model="benefactor.asset_seq">
                <option :value="undefined">{{
                  $t('admin.restriction_select')
                }}</option>
                <option
                  v-for="{ seq, title } in assets"
                  :key="seq"
                  :value="seq"
                >
                  {{ title }}
                </option>
              </select>
            </template>
            <template v-else>
              의
              <input
                v-model="benefactor.amount"
                type="text"
                :class="[$style.inputTypeText, $style.w120, $style.tRight]"
              />
              %를
            </template>
            지급
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <a
              v-if="benefactor.seq"
              href="#"
              :class="[$style.button]"
              @click="handleSubmitCondition(idx)"
              ><span>{{ $t('admin.modify') }}</span></a
            >
            <a
              v-else
              href="#"
              :class="[$style.black, $style.button]"
              @click="handleSubmitCondition(idx)"
              ><span>{{ $t('admin.enroll') }}</span></a
            >
            {{ ' ' }}
            <a
              href="#"
              :class="[$style.red, $style.button]"
              @click="handleDeleteCondition(idx)"
              ><span>{{ $t('admin.delete') }}</span></a
            >
          </td>
        </tr>
        <tr v-if="!benefactors.length">
          <td :class="$style.tCenter" colspan="4">{{
            $t('affiliate_policy_benefactor.msg_allowance_policy_no')
          }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
.adminRightExtra p {
  margin: 10px 0;
}
</style>
